<script>
  import { _, number } from 'svelte-i18n';
  import BottomSheet from './BottomSheet.svelte';
  import ItemsListing from './ItemsListing.svelte';
  import Checkout from './Checkout.svelte';
  import Countdown from './Countdown.svelte';
  import Starburst from './Starburst.svelte';
  import defaultArt from '../assets/web_battle_pass/banner.png?format=webp;jpg';

  // `pack` is the payload emitted by StorePack's compact card, or null when closed.
  let { pack = null, onClose, onBuy } = $props();

  // 'detail' shows the pack; 'checkout' swaps the payment step into this same
  // sheet, so mobile reuses the pack sheet instead of a separate centred modal.
  let stage = $state('detail');

  // Always reopen on the detail view (reset whenever the pack changes / closes).
  $effect(() => {
    pack;
    stage = 'detail';
  });

  function buy() {
    if (!pack) return;
    // Gift packs run a separate recipient/note flow handled by the parent.
    if (pack.storeSection === 'giftFriend') {
      onBuy?.({ storePack: pack.storePack, packDetails: pack.packDetails, storeSection: pack.storeSection });
    } else {
      stage = 'checkout';
    }
  }
</script>

<BottomSheet open={!!pack} {onClose} title={pack ? $_(pack.packDetails.title) : ''}>
  {#if pack && stage === 'checkout'}
    <Checkout
      storePack={pack.storePack}
      packDetails={pack.packDetails}
      payments_modal_hide={onClose}
      embedded={true}
    />
  {:else if pack}
    <div class="pd {pack.rarityClass}">
      <div class="pd-art">
        {#if pack.imageSrc}
          <img src={pack.imageSrc} alt={$_(pack.packDetails.title)} />
        {:else}
          <!-- Packs without their own art (e.g. subscriptions) get the battle
               pass banner as a default so the header isn't an empty band. -->
          <picture>
            <source srcset={defaultArt[0]} type="image/webp">
            <source srcset={defaultArt[1]} type="image/jpg">
            <img src={defaultArt[0]} alt={$_(pack.packDetails.title)} />
          </picture>
        {/if}
        <!-- Title sits on the art (like the pack listing), so the band below shows
             only the "Receive X Gold" reward line. -->
        <div class="pd-arttitle">
          <h2 class="pd-title">{@html $_(pack.packDetails.title)}</h2>
        </div>
        <!-- Same bonus/discount starburst the compact card showed. Top-left:
             the sheet's ✕ close button owns the top-right corner. -->
        {#if pack.badge}
          <Starburst label={pack.badge.label}>{pack.badge.text}</Starburst>
        {/if}
      </div>

      <div class="pd-banner">
        {#if pack.packDetails.subTitle && pack.packDetails.subTitle !== pack.packDetails.title}
          <p class="pd-subtitle">{$_(pack.packDetails.subTitle)}</p>
        {/if}
        {#if pack.packDetails.description}
          <p class="pd-desc">{@html $_(pack.packDetails.description, { values: { GOLD_VALUE: $number(pack.goldWorth ?? 0) } })}</p>
        {/if}
      </div>

      <div class="pd-items">
        <ItemsListing
          storePack={pack.storePack}
          goldAmount={pack.goldAmount}
          globals={pack.globals}
          items={pack.items}
          hasMask={false}
        />
      </div>
    </div>
  {/if}

  {#snippet footer()}
    {#if pack && stage === 'detail'}
      <!-- "Time left" under the item listing / above the buy action, like the
           desktop card. Uses endAt (store packs) or finishAt (subscriptions). -->
      {#if pack.storePack?.endAt || pack.storePack?.finishAt}
        <div class="pd-timer">
          <Countdown endDate={pack.storePack.endAt ?? pack.storePack.finishAt}>
            {$_('IncrementalPacksTimeLeft')}
          </Countdown>
        </div>
      {/if}
      {#if pack.priceString && pack.priceString !== 'Price not available'}
        <!-- Buy button styled like the pack-listing price plate: discount badge on
             the left, strikethrough full price above the current price. -->
        <button type="button" class="pd-buy-plate" onclick={buy}>
          {#if pack.discountBadgeSrc}
            <img class="pd-badge" alt="Discount" src={pack.discountBadgeSrc} />
          {/if}
          <span class="pd-prices">
            {#if pack.fullPriceString}<s class="pd-was">{pack.fullPriceString}</s>{/if}
            <span class="pd-now">{pack.priceString}</span>
          </span>
        </button>
      {:else}
        <div class="pd-footer"><span class="pd-amount">{pack.priceString}</span></div>
      {/if}
    {/if}
  {/snippet}
</BottomSheet>

<style lang="scss">
  @use '../styles/breakpoints.scss' as bp;
  @use '../styles/rarity.scss' as rar;

  // The whole content area is a bright rarity-tinted card for high contrast,
  // matching the desktop pack body. The dark sheet shows only as a thin frame.
  .pd {
    @include rar.rarity-palette;
    background: var(--r-body);
    border-radius: 14px;
    overflow: hidden;
    color: var(--r-ink);
  }

  .pd-art {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 7; // shorter than the card; crops the dark band
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center 30%;
      display: block;
    }

    // Badge fully inside the art: the sheet clips its rounded corners and the
    // ✕ close button sits at the top-right, so anchor it top-left instead of
    // the card's overhanging top-right position. Cap the label sizes so values
    // like "5,700%" fit inside the star (same trick as the compact cards).
    :global(.starburst) {
      top: 8px;
      left: 8px;
      right: auto;
      --starburst-font: 12px;
      --starburst-small-font: 10px;
      --starburst-pad: 0 4px;
    }
  }

  // Title overlaid on the lower edge of the art (like the pack listing), with a scrim.
  .pd-arttitle {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 24px 14px 8px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 30%, rgba(0, 0, 0, 0));
    text-align: center;
  }

  .pd-banner {
    @include rar.rarity-header-bg;
    padding: 10px 14px;
    text-align: center;
  }

  .pd-title {
    margin: 0;
    font-family: var(--font-family-sans-condensed);
    font-size: 28px;
    line-height: 1.1;
    font-weight: 800;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
  }

  // Now the primary text in the band ("Receive X Gold") — cream + prominent,
  // matching the desktop sub-header / pack-listing reward band.
  .pd-subtitle {
    margin: 0;
    font-family: var(--font-family-sans-condensed);
    font-size: 20px;
    font-weight: 800;
    color: #feffd6;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
  }

  // Descriptive line below the subtitle (matches the desktop card's small print);
  // lighter and smaller, with bolded numbers that arrive wrapped in <b>.
  .pd-desc {
    margin: 4px 0 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.92);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);

    :global(b) {
      font-weight: 800;
    }
  }

  .pd-items {
    padding: 8px 10px 4px;

    // ItemsListing positions its .items with a top margin meant for the desktop
    // card's absolute header; neutralize it and let it sit on the bright body.
    :global(.items) {
      margin-top: 0;
      background: rgba(255, 255, 255, 0.28);
    }
    :global(.items .mask) {
      height: auto;
      max-height: 46vh;
    }

    // Items rendered ~2x larger so quantities are easy to read (seniors play too).
    // The atlas sprite is a responsive ::after that fills the icon box, so growing
    // the box grows the sprite. The temporary-item border lives inside the icon's
    // positioning box (Item.svelte) so it scales with the icon automatically.
    :global(.item) {
      // min-height, not height: titles wrap on narrow screens and a long name
      // (3-4 lines) must grow the row instead of clipping at a fixed 84px.
      min-height: 84px;
      height: auto;
      padding: 6px 8px;
    }
    :global(.item .item-icon) {
      width: 72px;
      height: 72px;
      max-width: 72px;
      max-height: 72px;
    }
    :global(.item .item-title) {
      font-size: 16px;
      padding-left: 14px;
      padding-right: 14px;
      // Wrap instead of the mobile nowrap+marquee: the 84px rows fit two lines,
      // so the full name is always readable (no text stranded mid-scroll).
      white-space: normal;
      line-height: 1.2;
    }
    :global(.item .item-amount) {
      // Base size for the amount column; Item.svelte scales long amounts down
      // from this so "30,000x" can't push the icon out of line.
      --amt-base: 26px;
    }
    :global(.item .greenCheckmark) {
      bottom: 8px;
      right: 8px;
    }
  }

  .pd-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
  }

  .pd-timer {
    text-align: center;
    margin-bottom: 8px;
    font-size: 15px;
    font-weight: 700;
    color: #ffdca8;
  }

  .pd-amount {
    color: #ffd84d;
    font-weight: 900;
    font-size: 44px; // headline price — biggest, most legible element of the sheet
    line-height: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }

  // Buy button as the desktop/compact orange price plate: badge left, prices centred.
  .pd-buy-plate {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 60px;
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: radial-gradient(ellipse at center, #ffcc00 10%, #ff7700 100%);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3),
      inset 0 2px 0 0 rgba(255, 255, 255, 0.45),
      inset 0 -2px 1px 1px rgba(0, 0, 0, 0.3);
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.5);
  }

  .pd-badge {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%) rotate(-10deg);
    width: 60px;
    z-index: 1;
    pointer-events: none;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
  }

  .pd-prices {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.05;
    min-width: 0;
  }

  .pd-was {
    color: rgba(0, 0, 0, 0.5);
    font-size: 16px;
    font-weight: 700;
    text-decoration: line-through;
    white-space: nowrap;
  }

  .pd-now {
    color: rgba(0, 0, 0, 0.85);
    font-weight: 900;
    font-size: 30px;
    line-height: 1.05;
    white-space: nowrap;
  }
</style>
